import React, { useState, useEffect, useRef } from "react";
import { RadioButton } from "primereact/radiobutton";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { fetchForms } from "../FormsList/api";

const InputComponent = ({
  onSave,
  closeSidebar,
  fieldName,
  type,
  required,
  minValue,
  maxValue,
  linked,
  database,
  field,
  maxLength,
}) => {
  const [selectedType, setSelectedType] = useState(type || "");
  const [isRequired, setIsRequired] = useState(required || false);
  const [isLinked, setIsLinked] = useState(linked || false);
  const [showMaxLength, setShowMaxLength] = useState(false);
  const [showMinMax, setShowMinMax] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState(database || null);
  const [showFieldsDropdown, setShowFieldsDropdown] = useState(false);
  const [selectedField, setSelectedField] = useState(field || null);
  const [maxLengthState, setMaxLengthState] = useState(maxLength || "");
  const [minValueState, setMinValueState] = useState(minValue || "");
  const [maxValueState, setMaxValueState] = useState(maxValue || "");
  const toast = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalDatabase, setModalDatabase] = useState(null);
  const [modalField, setModalField] = useState(null);
  const [formsList, setFormsList] = useState([]);
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getForms = async () => {
      setIsLoading(true);
      try {
        const forms = await fetchForms();
        setFormsList(forms);
      } catch (error) {
        console.error("Error fetching forms:", error);
      } finally {
        setIsLoading(false);
      }
    };
    getForms();
  }, []);

  const handleModalSave = () => {
    if (!modalDatabase || !modalField) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select both form and field",
        life: 3000,
      });
      return;
    }

    const fieldProperties = modalDatabase.properties[modalField];

    setSelectedType(fieldProperties.type || "");
    setIsRequired(fieldProperties.required === "yes");
    setMaxLengthState(fieldProperties.maxLength || "");
    setMinValueState(fieldProperties.min || "");
    setMaxValueState(fieldProperties.max || "");

    setShowModal(false);
    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Field properties imported successfully",
      life: 3000,
    });
  };

  const filterFieldsByType = (properties, allowedTypes) => {
    return Object.entries(properties).reduce((acc, [key, value]) => {
      if (allowedTypes.includes(value.type)) {
        acc[key] = value;
      }
      return acc;
    }, {});
  };

  useEffect(() => {
    if (selectedType === "text") {
      setShowMaxLength(true);
      setShowMinMax(false);
    } else if (selectedType === "number") {
      setShowMaxLength(false);
      setShowMinMax(true);
    } else {
      setShowMaxLength(false);
      setShowMinMax(false);
    }
  }, [selectedType]);

  useEffect(() => {
    if (isLinked) {
      setShowFieldsDropdown(true);
    } else {
      setShowFieldsDropdown(false);
      setSelectedDatabase(null);
      setSelectedField(null);
    }
  }, [isLinked]);

  useEffect(() => {
    if (selectedDatabase) {
      setShowFieldsDropdown(true);
    } else {
      setShowFieldsDropdown(false);
    }
  }, [selectedDatabase]);

  const handleSave = () => {
    if (!selectedType) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a type before saving.",
        life: 3000,
      });
      return;
    }

    if (selectedType === "text" && !maxLengthState) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter a max length for text type.",
        life: 3000,
      });
      return;
    }

    if (selectedType === "number") {
      if (!minValueState || !maxValueState) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail: "Please enter both min and max values for number type.",
          life: 3000,
        });
        return;
      }
      if (parseFloat(maxValueState) <= parseFloat(minValueState)) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail: "Max value must be greater than min value.",
          life: 3000,
        });
        return;
      }
      const MIN_32BIT_INT = -2147483648;
      const MAX_32BIT_INT = 2147483647;

      if (
        parseFloat(minValueState) < MIN_32BIT_INT ||
        parseFloat(maxValueState) > MAX_32BIT_INT
      ) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail:
            "Values exceed 32-bit integer limits (-2,147,483,648 to 2,147,483,647).",
          life: 4000,
        });
        return;
      }
    }

    const fieldData = {
      name: fieldName,
      type: selectedType,
      required: isRequired,
      maxLength: selectedType === "text" ? maxLengthState : undefined,
      minValue: selectedType === "number" ? minValueState : undefined,
      maxValue: selectedType === "number" ? maxValueState : undefined,
      linked: isLinked,
      database: selectedDatabase,
      field: selectedField,
      databaseId: selectedDatabase ? selectedDatabase.id : undefined,
    };
    const saveSuccessful = onSave(fieldData);
    if (saveSuccessful) {
      closeSidebar();
    }
  };

  return (
    <div className="p-field">
      <Toast ref={toast} />
      <Divider />
      <div className="p-field mb-3">
        <Button
          label="Import"
          icon="pi pi-download"
          style={{
            backgroundColor: "#D97A07",
            border: "none",
            marginBottom: "1rem",
          }}
          onClick={() => setShowModal(true)}
        />
      </div>

      <Dialog
        header="Import Field Properties"
        visible={showModal}
        onHide={() => setShowModal(false)}
        style={{ width: "50vw" }}
      >
        <div className="p-field mb-3">
          <label
            htmlFor="modalDatabase"
            className="block text-900 font-medium mb-2"
          >
            Select Form
          </label>
          <Dropdown
            filter
            id="modalDatabase"
            value={modalDatabase}
            options={
              formsList && formsList.length > 0
                ? formsList.map((form) => ({
                    label: form.title,
                    value: form,
                  }))
                : []
            }
            onChange={(e) => setModalDatabase(e.value)}
            placeholder="Select a Form"
            className="w-full mb-3"
          />
        </div>

        {modalDatabase && modalDatabase.properties && (
          <div className="p-field mb-3">
            <label
              htmlFor="modalField"
              className="block text-900 font-medium mb-2"
            >
              Select Field
            </label>
            <Dropdown
              filter
              id="modalField"
              value={modalField}
              options={Object.entries(
                filterFieldsByType(modalDatabase.properties, [
                  "text",
                  "number",
                  "email",
                ])
              ).map(([key, value]) => ({
                label: key,
                value: key,
              }))}
              onChange={(e) => setModalField(e.value)}
              placeholder="Select a Field"
              className="w-full mb-3"
            />
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "1rem",
          }}
        >
          <Button
            label="Import"
            icon="pi pi-check"
            style={{
              backgroundColor: "#D97A07",
              border: "none",
            }}
            onClick={handleModalSave}
          />
        </div>
      </Dialog>
      <label className="block text-900 font-medium mb-3">Type : </label>
      <div className="p-formgroup-inline mb-3">
        <div className="p-field-radiobutton mb-2">
          <RadioButton
            inputId="text"
            name="type"
            value="text"
            onChange={(e) => setSelectedType(e.value)}
            checked={selectedType === "text"}
            required
          />
          <label htmlFor="text">Text</label>
        </div>
        <div className="p-field-radiobutton mb-2">
          <RadioButton
            inputId="number"
            name="type"
            value="number"
            onChange={(e) => setSelectedType(e.value)}
            checked={selectedType === "number"}
            required
          />
          <label htmlFor="number">Number</label>
        </div>
        <div className="p-field-radiobutton">
          <RadioButton
            inputId="email"
            name="type"
            value="email"
            onChange={(e) => setSelectedType(e.value)}
            checked={selectedType === "email"}
            required
          />
          <label htmlFor="email">Email</label>
        </div>
      </div>

      {showMaxLength && (
        <div className="p-field">
          <InputText
            placeholder="Max length"
            type="number"
            value={maxLengthState}
            onChange={(e) => setMaxLengthState(e.target.value)}
            className="w-full mb-3"
          />
        </div>
      )}

      {showMinMax && (
        <div className="p-formgroup-inline">
          <div className="p-field">
            <InputText
              placeholder="Min"
              type="number"
              value={minValueState}
              onChange={(e) => setMinValueState(e.target.value)}
              className="w-full mb-3"
            />
          </div>
          <div className="p-field">
            <InputText
              placeholder="Max"
              type="number"
              value={maxValueState}
              onChange={(e) => setMaxValueState(e.target.value)}
              className="w-full mb-3"
            />
          </div>
        </div>
      )}

      <Divider />
      <div className="p-field-checkbox mb-3">
        <label className="font-medium" htmlFor="required">
          Required :{" "}
        </label>
        <InputSwitch
          inputId="required"
          checked={isRequired}
          onChange={(e) => setIsRequired(e.value)}
          style={{ marginLeft: "1rem", marginTop: "0.5rem" }}
        />
      </div>
      <Divider />
      <div className="p-field mb-3">
        <label htmlFor="linked" className="block text-900 font-medium mb-2">
          Link it to an existing DB :
          <InputSwitch
            checked={isLinked}
            onChange={(e) => setIsLinked(e.value)}
            style={{ marginLeft: "1rem", marginTop: "0.5rem" }}
          />
        </label>
      </div>
      {isLinked && (
        <div className="p-field mb-3">
          <Divider />
          <label
            htmlFor="databases"
            className="block text-900 font-medium mb-2"
          >
            Forms
          </label>
          <Dropdown
            filter
            id="databases"
            value={selectedDatabase}
            options={
              formsList && formsList.length > 0
                ? formsList.map((form) => ({
                    label: form.title,
                    value: form.id,
                  }))
                : []
            }
            onChange={(e) => setSelectedDatabase(e.value)}
            placeholder="Select a Form"
            className="w-full mb-3"
          />
          {showFieldsDropdown && selectedDatabase && (
            <div className="p-field">
              <Divider />
              <label
                htmlFor="fields"
                className="block text-900 font-medium mb-2"
              >
                Select Field
              </label>
              <Dropdown
                filter
                id="fields"
                value={selectedField}
                options={
                  formsList.find((form) => form.id === selectedDatabase)
                    ?.properties
                    ? Object.keys(
                        formsList.find((form) => form.id === selectedDatabase)
                          .properties
                      ).map((key) => ({
                        label: key,
                        value: key,
                      }))
                    : []
                }
                onChange={(e) => setSelectedField(e.value)}
                placeholder="Select a Field"
                className="w-full mb-3"
              />
            </div>
          )}
        </div>
      )}
      <Button
        label="Save"
        icon="pi pi-check"
        style={{
          color: "#fff",
          backgroundColor: "#0D2E4D",
          border: "none",
          fontFamily: "poppins",
        }}
        onClick={handleSave}
      />
    </div>
  );
};

export default InputComponent;
